import React, {
    useState, useRef, useEffect, useCallback
} from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Text, Flex, Divider, Link
} from 'components/ui';
import RichText from 'components/Content/RichText';
import { lineHeights, fontSizes } from 'style/config';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import keyConsts from 'utils/KeyConstants';
import { DebouncedResize } from 'constants/events';
import CatalogEN from 'components/Catalog/locales/Catalog_en_US';
import CatalogFR from 'components/Catalog/locales/Catalog_fr_CA';

const { getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(CatalogEN, CatalogFR);

const FAQCollection = ({ faqHeader, faqCollection }) => {
    const [hasOverflow, setHasOverflow] = useState(false);
    const [showMore, setShowMore] = useState(false);
    const contentRef = useRef(null);

    const lineHeight = lineHeights.relaxed * fontSizes.base;

    const handleResize = useCallback(() => {
        if (!showMore && contentRef.current) {
            setHasOverflow(contentRef.current.scrollHeight > contentRef.current.offsetHeight);
        }
    }, [showMore]);

    const handleFocusWithin = useCallback(
        e => {
            if (!showMore) {
                switch (e.key) {
                    case keyConsts.UP:
                    case keyConsts.DOWN:
                    case keyConsts.LEFT:
                    case keyConsts.RIGHT:
                    case keyConsts.TAB:
                        toggleOverflow();

                        break;
                    default:
                        break;
                }
            }
        },
        [showMore]
    );

    const toggleOverflow = () => {
        const currentScroll = window.scrollY;
        setShowMore(prevShowMore => !prevShowMore);
        window.scroll(0, currentScroll);
    };

    useEffect(() => {
        handleResize();
        window.addEventListener(DebouncedResize, handleResize);

        if (contentRef.current) {
            contentRef.current.addEventListener('keyup', handleFocusWithin);
        }

        return () => {
            window.removeEventListener(DebouncedResize, handleResize);

            if (contentRef.current) {
                contentRef.current.removeEventListener('keyup', handleFocusWithin);
            }
        };
    }, [handleResize, handleFocusWithin]);

    useEffect(() => {
        if (!showMore) {
            handleResize();
        }
    }, [showMore]);

    return (
        <>
            <Box>
                <Text
                    as='h3'
                    fontSize='xl'
                    fontWeight='bold'
                    mb={4}
                >
                    {faqHeader}
                </Text>

                <Box
                    ref={contentRef}
                    lineHeight='relaxed'
                    overflow='hidden'
                    maxHeight={showMore || [lineHeight * 2, lineHeight * 3.5]}
                >
                    {faqCollection?.items?.map(faq => (
                        <Flex
                            key={faq.sid}
                            flexDirection='column'
                            gap={2}
                            width='100%'
                            mb={4}
                        >
                            <RichText content={faq.question} />
                            <RichText content={faq.answer} />
                            <Divider />
                        </Flex>
                    ))}
                </Box>
                {hasOverflow && (
                    <Text
                        is='p'
                        lineHeight='relaxed'
                    >
                        <Link
                            onClick={toggleOverflow}
                            color='blue'
                            padding={2}
                            margin={-2}
                            children={getText(showMore ? 'showLess' : 'showMore')}
                        />
                    </Text>
                )}
            </Box>
            <Divider mt={7} />
        </>
    );
};

FAQCollection.propTypes = {
    faqHeader: PropTypes.string,
    faqCollection: PropTypes.shape({
        items: PropTypes.arrayOf(
            PropTypes.shape({
                question: PropTypes.object,
                answer: PropTypes.object,
                sid: PropTypes.string
            })
        )
    })
};

export default wrapFunctionalComponent(FAQCollection, 'FAQCollection');
